import { Component, OnInit } from '@angular/core';
import {
  Chart,
  LinearScale,
  LineController,
  CategoryScale,
  PointElement,
  LineElement,
  BarElement,
  DoughnutController,
  BarController,
  ArcElement,
  ChartType,
} from 'chart.js';
import { JsonRequest } from '../../model/jsonRequest.model';
import { AppService } from '../../core/service/app.service';
import { CoreApiService } from '../../core/service/core-api-service';
import { properties } from '../../../environments/properties';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';

Chart.register(
  LinearScale,
  LineController,
  CategoryScale,
  PointElement,
  LineElement,
  BarElement,
  DoughnutController,
  BarController,
  ArcElement
);

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [TableModule, CommonModule, DropdownModule, FormsModule],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit {
  public doughnutChartType: ChartType = 'doughnut';

  data: any;
  chartCountUser!: Chart<'doughnut', number[], string>;
  userData: any[] = [];
  totalUsers: number = 0;

  chartCountDriver!: Chart<'doughnut', number[], string>;
  driverData: any[] = [];
  totalDrivers: number = 0;

  chartCountCarTrip!: Chart<'doughnut', number[], string>;
  carTripData: any[] = [];
  totalCarTrip: number = 0;

  monthsList: any[] = [];
  selectedPeriod: any = null;

  selectedMonth!: number;
  selectedYear!: number;

  selectedStatus: any = null;
  statusCarTrip: any[] = [
    {
      label: 'Todos',
      value: 0,
    },
    {
      label: 'Cancelado Cliente',
      value: 3,
    },
    {
      label: 'Cancelado Conductor',
      value: 4,
    },
    {
      label: 'Finalizados',
      value: 5,
    },
  ];

  constructor(
    private readonly apiService: CoreApiService, 
    private readonly util: AppService) {}

  ngOnInit() {
    this.generateLastMonths();
    this.getInfoDashboard();
    this.getCountUserByGroupState();
    this.getCountDriversByGroupState();
  }

  generateLastMonths() {
    const today = new Date();
    this.monthsList = [];

    for (let i = 0; i < 6; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);

      this.monthsList.push({
        label: this.getMonthName(date.getMonth()) + ' ' + date.getFullYear(),
        month: date.getMonth() + 1,
        year: date.getFullYear(),
      });
    }
    this.selectedPeriod = this.monthsList[0];
    this.selectedMonth = this.monthsList[0].month;
    this.selectedYear = this.monthsList[0].year;
    this.selectedStatus = this.statusCarTrip[0];
    this.getCountCarTripByGroupState(
      this.selectedYear,
      this.selectedMonth,
      this.selectedStatus.value
    );
  }

  getMonthName(monthIndex: number): string {
    const months = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];
    return months[monthIndex];
  }

  onPeriodChange(event: any) {
    this.selectedMonth = event.value.month;
    this.selectedYear = event.value.year;
    this.getCountCarTripByGroupState(
      this.selectedYear,
      this.selectedMonth,
      this.selectedStatus.value
    );
  }

  onStatusChange(event: any) {
    this.getCountCarTripByGroupState(
      this.selectedYear,
      this.selectedMonth,
      this.selectedStatus.value
    );
  }

  getInfoDashboard() {
    const body: JsonRequest = {
      data: JSON.stringify([{}]),
      company: this.util.returnEmpresa(),
      fecha: new Date(),
    };
    this.apiService.consumirWSPost(
      properties.serviceDashboard,
      body,
      (data) => {
        if (data.estado === properties.codeOK) {
          this.data = data.data;
        } else {
          this.util.mostrarToast(
            'Información',
            'Error al obtener la información',
            4
          );
        }
      }
    );
  }

  getCountUserByGroupState() {
    const body: JsonRequest = {
      data: JSON.stringify([{}]),
      company: this.util.returnEmpresa(),
      fecha: new Date(),
    };
    this.apiService.consumirWSGet(
      properties.serviceDashboardCountUserByGroupState,
      body,
      (data) => {
        if (data.estado === properties.codeOK) {
          this.userData = data.data;
          this.userData = data.data.map((item: any, index: number) => ({
            ...item,
            color: this.generateColor(index, data.data.length),
          }));
          const labels = this.userData.map((item) => item.itemLabel);
          const values = this.userData.map((item) => item.itemValue);
          const colors = this.userData.map((c) => c.color);
          this.totalUsers = this.userData.reduce((a, b) => a + b.itemValue, 0);

          this.createChartUsers(labels, values, colors);
        } else {
          this.util.mostrarToast(
            'Información',
            'Error al obtener la información',
            4
          );
        }
      }
    );
  }

  getCountDriversByGroupState() {
    const body: JsonRequest = {
      data: JSON.stringify([{}]),
      company: this.util.returnEmpresa(),
      fecha: new Date(),
    };
    this.apiService.consumirWSGet(
      properties.serviceDashboardCountDriverByGroupState,
      body,
      (data) => {
        if (data.estado === properties.codeOK) {
          this.driverData = data.data;
          this.driverData = data.data.map((item: any, index: number) => ({
            ...item,
            color: this.generateColor(index, data.data.length),
          }));
          const labels = this.driverData.map((item) => item.itemLabel);
          const values = this.driverData.map((item) => item.itemValue);
          const colors = this.driverData.map((c) => c.color);
          this.totalDrivers = this.driverData.reduce(
            (a, b) => a + b.itemValue,
            0
          );

          this.createChartDrivers(labels, values, colors);
        } else {
          this.util.mostrarToast(
            'Información',
            'Error al obtener la información',
            4
          );
        }
      }
    );
  }

  getCountCarTripByGroupState(year: number, month: number, status: number) {
    const body: JsonRequest = {
      data: JSON.stringify([{}]),
      company: this.util.returnEmpresa(),
      fecha: new Date(),
    };
    this.apiService.consumirWSGet(
      properties.serviceDashboardCountCarTripByGroupState +
        '/' +
        year +
        '/' +
        month +
        '/' +
        status,
      body,
      (data) => {
        if (data.estado === properties.codeOK) {
          this.carTripData = data.data;
          this.carTripData = data.data.map((item: any, index: number) => ({
            ...item,
            color: this.generateColor(index, data.data.length),
          }));
          const labels = this.carTripData.map((item) => item.itemLabel);
          const values = this.carTripData.map((item) => item.itemValue);
          const colors = this.carTripData.map((c) => c.color);
          this.totalCarTrip = this.carTripData.reduce(
            (a, b) => a + b.itemValue,
            0
          );

          this.createChartCarTrip(labels, values, colors);
        } else {
          this.util.mostrarToast(
            'Información',
            'Error al obtener la información',
            4
          );
        }
      }
    );
  }

  generateColor(index: number, total: number): string {
    return `hsl(${(index * 360) / total}, 70%, 60%)`;
  }

  createChartUsers(labels: string[], values: number[], colors: string[]) {
    if (this.chartCountUser) {
      this.chartCountUser.destroy();
    }

    this.chartCountUser = new Chart('chart-users', {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'right',
          },
        },
      },
    });
  }

  createChartDrivers(labels: string[], values: number[], colors: string[]) {
    if (this.chartCountDriver) {
      this.chartCountDriver.destroy();
    }

    this.chartCountDriver = new Chart('chart-drivers', {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'right',
          },
        },
      },
    });
  }

  createChartCarTrip(labels: string[], values: number[], colors: string[]) {
    if (this.chartCountCarTrip) {
      this.chartCountCarTrip.destroy();
    }

    this.chartCountCarTrip = new Chart('chart-car-trip', {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'right',
          },
        },
      },
    });
  }
}
